/**
 * Frappe API Client
 * Provides type-safe API calls to the Frappe backend with session management
 */

import { logger } from './logger';

// API Configuration
// In dev mode with Vite proxy, use empty string for relative URLs (proxy handles /api/*)
// In production, set VITE_API_URL to the full Frappe URL
const API_BASE_URL = import.meta.env.VITE_API_URL || '';

// Storage keys
const STORAGE_KEYS = {
  API_KEY: 'frappe_api_key',
  SID: 'frappe_sid',
  USER: 'frappe_user',
  USER_DATA: 'frappe_user_data',
  EXPIRES_AT: 'frappe_expires_at',
} as const;

// Session duration (30 days in milliseconds)
const SESSION_DURATION = 30 * 24 * 60 * 60 * 1000;

// Types for API responses
export interface FrappeResponse<T = unknown> {
  message: T;
  exc?: string;
  exc_type?: string;
  _server_messages?: string;
}

export interface FrappeError {
  message: string;
  exc_type?: string;
  indicator?: 'red' | 'orange' | 'yellow';
  http_status_code?: number;
}

// Parse server messages from Frappe
function parseServerMessages(serverMessages?: string): string[] {
  if (!serverMessages) return [];
  try {
    const parsed = JSON.parse(serverMessages);
    return Array.isArray(parsed)
      ? parsed.map(m => {
          try {
            const inner = JSON.parse(m);
            return inner.message || m;
          } catch {
            return m;
          }
        })
      : [parsed];
  } catch {
    return [serverMessages];
  }
}

// Custom error class for API errors
export class FrappeApiError extends Error {
  public statusCode: number;
  public excType?: string;
  public serverMessages: string[];
  public isAuthError: boolean;

  constructor(message: string, statusCode: number, excType?: string, serverMessages: string[] = []) {
    super(message);
    this.name = 'FrappeApiError';
    this.statusCode = statusCode;
    this.excType = excType;
    this.serverMessages = serverMessages;
    this.isAuthError = statusCode === 401 || statusCode === 403 || excType === 'AuthenticationError';
  }
}

// Session expired event
const SESSION_EXPIRED_EVENT = 'frappe:session_expired';

export function onSessionExpired(callback: () => void): () => void {
  window.addEventListener(SESSION_EXPIRED_EVENT, callback);
  return () => window.removeEventListener(SESSION_EXPIRED_EVENT, callback);
}

function emitSessionExpired(): void {
  window.dispatchEvent(new CustomEvent(SESSION_EXPIRED_EVENT));
}

// Get stored auth token
function getAuthToken(): string | null {
  return localStorage.getItem(STORAGE_KEYS.API_KEY);
}

// Get stored session ID
function getSessionId(): string | null {
  return localStorage.getItem(STORAGE_KEYS.SID);
}

// Get session expiry
function getSessionExpiry(): number | null {
  const expiry = localStorage.getItem(STORAGE_KEYS.EXPIRES_AT);
  return expiry ? parseInt(expiry, 10) : null;
}

// Check if session is expired
function isSessionExpired(): boolean {
  const expiry = getSessionExpiry();
  if (!expiry) return true;
  return Date.now() > expiry;
}

// Store auth credentials
export function setAuthCredentials(
  apiKey: string,
  sid: string,
  userData?: { email: string; full_name: string; role: string }
): void {
  if (apiKey) {
    localStorage.setItem(STORAGE_KEYS.API_KEY, apiKey);
  }
  if (sid) {
    localStorage.setItem(STORAGE_KEYS.SID, sid);
  }
  if (userData) {
    localStorage.setItem(STORAGE_KEYS.USER, userData.email);
    localStorage.setItem(STORAGE_KEYS.USER_DATA, JSON.stringify(userData));
  }
  // Set expiry for 30 days
  localStorage.setItem(STORAGE_KEYS.EXPIRES_AT, String(Date.now() + SESSION_DURATION));
}

// Clear auth credentials
export function clearAuthCredentials(): void {
  Object.values(STORAGE_KEYS).forEach(key => localStorage.removeItem(key));
}

// Get stored user data
export function getStoredUser(): { email: string; full_name: string; role: string } | null {
  const userData = localStorage.getItem(STORAGE_KEYS.USER_DATA);
  if (!userData) return null;
  try {
    return JSON.parse(userData);
  } catch {
    return null;
  }
}

// Check if user is authenticated
export function isAuthenticated(): boolean {
  const hasCredentials = !!(getAuthToken() || getSessionId());
  if (!hasCredentials) return false;

  // Check if session is expired
  if (isSessionExpired()) {
    clearAuthCredentials();
    return false;
  }

  return true;
}

// Extend session
export function extendSession(): void {
  if (isAuthenticated()) {
    localStorage.setItem(STORAGE_KEYS.EXPIRES_AT, String(Date.now() + SESSION_DURATION));
  }
}

// Build headers for API requests
function buildHeaders(includeAuth: boolean = true): HeadersInit {
  const headers: HeadersInit = {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
    'X-Frappe-CSRF-Token': getCsrfToken() || '',
  };

  if (includeAuth) {
    const apiKey = getAuthToken();
    if (apiKey) {
      headers['Authorization'] = `token ${apiKey}`;
    }
  }

  return headers;
}

// Get CSRF token from cookie
function getCsrfToken(): string | null {
  const match = document.cookie.match(/csrf_token=([^;]+)/);
  return match ? match[1] : null;
}

// Main API call function
export async function frappeCall<T = unknown>(
  method: string,
  params: Record<string, unknown> = {},
  options: {
    includeAuth?: boolean;
    httpMethod?: 'GET' | 'POST';
    suppressAuthError?: boolean;
  } = {}
): Promise<T> {
  const { includeAuth = true, httpMethod = 'POST', suppressAuthError = false } = options;
  const startTime = performance.now();

  // Log API request
  logger.debug('api', `API Request: ${httpMethod} ${method}`, {
    method,
    endpoint: method,
    httpMethod,
    params: Object.keys(params).length > 0 ? params : undefined,
    includeAuth,
  });

  // Check session before making authenticated calls
  if (includeAuth && !isAuthenticated()) {
    logger.warn('api', `API Request blocked: Session expired`, { method });
    if (!suppressAuthError) {
      emitSessionExpired();
    }
    throw new FrappeApiError('Session expired', 401, 'AuthenticationError');
  }

  let requestUrl = `${API_BASE_URL}/api/method/${method}`;

  const fetchOptions: RequestInit = {
    method: httpMethod,
    headers: buildHeaders(includeAuth),
    credentials: 'include',
  };

  if (httpMethod === 'GET') {
    const searchParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        if (typeof value === 'object') {
          searchParams.append(key, JSON.stringify(value));
        } else {
          searchParams.append(key, String(value));
        }
      }
    });
    const qs = searchParams.toString();
    if (qs) {
      requestUrl += `?${qs}`;
    }
  } else {
    fetchOptions.body = JSON.stringify(params);
  }

  try {
    const response = await fetch(requestUrl, fetchOptions);
    const duration = Math.round(performance.now() - startTime);

    // Handle authentication errors
    if (response.status === 401) {
      logger.warn('auth', `API Auth Error: ${method}`, {
        method,
        statusCode: response.status,
        duration,
      });
      clearAuthCredentials();
      if (!suppressAuthError) {
        emitSessionExpired();
      }
      throw new FrappeApiError('Session expired', response.status, 'AuthenticationError');
    }

    // 403 is a per-call permission denial (e.g. a teacher opening a page that
    // asks for a student-only method). The session itself is still valid, so
    // surface it as a normal API error instead of logging the user out.
    if (response.status === 403) {
      logger.warn('auth', `API Permission Denied: ${method}`, { method, duration });
      let denied = 'Permission denied';
      try {
        const body = await response.json();
        denied =
          parseServerMessages(body?._server_messages)[0] ||
          (typeof body?.message === 'string' ? body.message : '') ||
          denied;
      } catch { /* non-JSON body */ }
      throw new FrappeApiError(denied, 403, 'PermissionError');
    }

    // Handle HTTP errors
    if (!response.ok) {
      let errorMessage = `HTTP ${response.status}: ${response.statusText}`;
      let excType: string | undefined;
      let serverMessages: string[] = [];

      try {
        const errorData = await response.json() as FrappeResponse;
        if (errorData.exc) {
          const excMatch = errorData.exc.match(/(?:frappe\.exceptions\.)?(\w+Error|\w+Exception): (.+)/);
          if (excMatch) {
            excType = excMatch[1];
            errorMessage = excMatch[2];
          }
        }
        if (errorData._server_messages) {
          serverMessages = parseServerMessages(errorData._server_messages);
          if (serverMessages.length > 0) {
            errorMessage = serverMessages[0];
          }
        }
      } catch {
        // Could not parse error response
      }

      logger.error('api', `API Error: ${method}`, {
        method,
        statusCode: response.status,
        errorMessage,
        excType,
        serverMessages,
        duration,
      });

      throw new FrappeApiError(errorMessage, response.status, excType, serverMessages);
    }

    const data = await response.json() as FrappeResponse<T>;

    // Check for Frappe-level errors
    if (data.exc) {
      const serverMessages = parseServerMessages(data._server_messages);
      logger.error('api', `API Frappe Error: ${method}`, {
        method,
        excType: data.exc_type,
        serverMessages,
        duration,
      });
      throw new FrappeApiError(
        serverMessages[0] || 'An error occurred',
        400,
        data.exc_type,
        serverMessages
      );
    }

    // Log successful response
    logger.debug('api', `API Response: ${method}`, {
      method,
      statusCode: response.status,
      duration,
      hasData: data.message !== null && data.message !== undefined,
    });

    // Extend session on successful authenticated calls
    if (includeAuth) {
      extendSession();
    }

    return data.message;
  } catch (error) {
    const duration = Math.round(performance.now() - startTime);

    if (error instanceof FrappeApiError) {
      throw error;
    }

    logger.error('api', `API Network Error: ${method}`, {
      method,
      error: error instanceof Error ? error.message : 'Unknown error',
      duration,
    });

    throw new FrappeApiError(
      error instanceof Error ? error.message : 'Network error',
      0
    );
  }
}

// Convenience function for GET requests
export async function frappeGet<T = unknown>(
  method: string,
  params: Record<string, unknown> = {},
  includeAuth: boolean = true
): Promise<T> {
  return frappeCall<T>(method, params, { includeAuth, httpMethod: 'GET' });
}

// Convenience function for POST requests
export async function frappePost<T = unknown>(
  method: string,
  params: Record<string, unknown> = {},
  includeAuth: boolean = true
): Promise<T> {
  return frappeCall<T>(method, params, { includeAuth, httpMethod: 'POST' });
}

// File upload function
export async function frappeUpload(
  file: File,
  options: {
    doctype?: string;
    docname?: string;
    fieldname?: string;
    isPrivate?: boolean;
    onProgress?: (percent: number) => void;
  } = {}
): Promise<{ file_url: string; file_name: string }> {
  if (!isAuthenticated()) {
    throw new FrappeApiError('Not authenticated', 401);
  }

  const formData = new FormData();
  formData.append('file', file);

  if (options.doctype) formData.append('doctype', options.doctype);
  if (options.docname) formData.append('docname', options.docname);
  if (options.fieldname) formData.append('fieldname', options.fieldname);
  formData.append('is_private', options.isPrivate ? '1' : '0');

  // Use XMLHttpRequest for progress tracking
  if (options.onProgress) {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();

      xhr.upload.addEventListener('progress', (e) => {
        if (e.lengthComputable && options.onProgress) {
          options.onProgress((e.loaded / e.total) * 100);
        }
      });

      xhr.addEventListener('load', () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          try {
            const data = JSON.parse(xhr.responseText);
            resolve(data.message);
          } catch {
            reject(new FrappeApiError('Invalid response', xhr.status));
          }
        } else {
          reject(new FrappeApiError('Upload failed', xhr.status));
        }
      });

      xhr.addEventListener('error', () => {
        reject(new FrappeApiError('Upload failed', 0));
      });

      xhr.open('POST', `${getApiBaseUrl()}/api/method/upload_file`);

      const apiKey = getAuthToken();
      if (apiKey) {
        xhr.setRequestHeader('Authorization', `token ${apiKey}`);
      }
      xhr.withCredentials = true;
      xhr.send(formData);
    });
  }

  // Standard fetch for simple uploads
  const headers: HeadersInit = {
    'Accept': 'application/json',
  };

  const apiKey = getAuthToken();
  if (apiKey) {
    headers['Authorization'] = `token ${apiKey}`;
  }

  const response = await fetch(`${getApiBaseUrl()}/api/method/upload_file`, {
    method: 'POST',
    headers,
    credentials: 'include',
    body: formData,
  });

  if (!response.ok) {
    throw new FrappeApiError('File upload failed', response.status);
  }

  const data = await response.json();
  return data.message;
}

// Get full URL for file paths
export function getFileUrl(path: string): string {
  if (!path) return '';
  if (path.startsWith('http')) return path;
  return `${API_BASE_URL}${path.startsWith('/') ? '' : '/'}${path}`;
}

// Get API base URL
export function getApiBaseUrl(): string {
  return API_BASE_URL;
}

// Validate session with server
export async function validateSession(): Promise<boolean> {
  if (!isAuthenticated()) {
    return false;
  }

  try {
    const response = await frappeCall<{ is_logged_in: boolean }>(
      'hudan_lms.api.auth.get_current_user',
      {},
      { includeAuth: true, suppressAuthError: true }
    );
    return response.is_logged_in === true;
  } catch {
    clearAuthCredentials();
    return false;
  }
}
